import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import BookingPage from "./pages/BookingPage";
import AboutPage from "./pages/AboutPage";
import AdminPage from "./pages/AdminPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import RefundPolicy from "./pages/RefundPolicy";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Send the httpOnly auth cookie on every axios request and let the browser
// store/clear it. We no longer persist the JWT in localStorage.
axios.defaults.withCredentials = true;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  // True until the initial /auth/me probe finishes. Lets gated routes
  // (e.g., /admin) wait instead of bouncing prematurely.
  const [authLoading, setAuthLoading] = useState(true);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Logout is best-effort — clear local state regardless.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Logout request failed (clearing local state anyway):", err?.message || err);
      }
    }
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Global axios interceptor — auto-logout on any 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // A 401 on /auth/me is expected when the user simply isn't logged in
          // (initial mount probe) — don't show the "session expired" toast for it.
          const url = error.config?.url || "";
          const detail = error.response?.data?.detail || "";
          const isAuthProbe = url.endsWith("/auth/me");
          // MFA_REQUIRED is *not* a session expiry — the admin's primary JWT
          // is still valid, they just need to clear the TOTP challenge. The
          // AdminMfaGate handles this in-component; don't log out here.
          const isMfaChallenge = detail === "MFA_REQUIRED" || url.includes("/admin/mfa/");
          if (isMfaChallenge) {
            return Promise.reject(error);
          }
          handleLogout();
          if (!isAuthProbe) {
            toast.error("Session expired. Please log in again.");
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [handleLogout]);

  // On mount: ask the backend who we are using the cookie (or any leftover
  // legacy Bearer token in localStorage during the transition window).
  useEffect(() => {
    const legacyToken = localStorage.getItem("token");
    const headers = legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {};
    axios
      .get(`${API}/auth/me`, { headers })
      .then((res) => {
        setUser(res.data);
        setIsAuthenticated(true);
        // Migration cleanup: once /auth/me succeeded, the legacy token is no
        // longer needed — the cookie is now the source of truth.
        if (legacyToken) localStorage.removeItem("token");
      })
      .catch((err) => {
        // Not authenticated — leave defaults. Suppress noise in production.
        if (process.env.NODE_ENV !== "production" && err?.response?.status !== 401) {
          // eslint-disable-next-line no-console
          console.warn("/auth/me probe failed:", err?.message || err);
        }
      })
      .finally(() => setAuthLoading(false));
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <LandingPage
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
                setUser={setUser}
              />
            }
          />
          <Route
            path="/booking"
            element={
              <BookingPage
                user={user}
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
                setUser={setUser}
                onLogout={handleLogout}
              />
            }
          />
          <Route path="/about" element={<AboutPage />} />
          {/* Iter 70 — dedicated shareable enquiry route.
              Renders the landing page with the enquiry modal auto-opened
              so the user has a clean https://travelsate.in/enquiry URL
              to post on social media. Also supports ?enquiry=1 on `/`. */}
          <Route
            path="/enquiry"
            element={
              <LandingPage
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
                setUser={setUser}
                openEnquiryOnMount
              />
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          {/* Admin URL slug is overridable via REACT_APP_ADMIN_URL_SLUG so it
              can be rotated to a non-guessable path in production without code
              changes. Defaults to "admin" for backwards compatibility. */}
          <Route
            path={`/${(process.env.REACT_APP_ADMIN_URL_SLUG || "admin").replace(/^\/+/, "")}`}
            element={
              <AdminPage user={user} isAuthenticated={isAuthenticated} authLoading={authLoading} />
            }
          />
        </Routes>
        <CookieConsentBanner />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
